const plugin = require('tailwindcss/plugin');
const values = require('lodash/values');
const flattenDeep = require('lodash/flattenDeep');
const range = require('lodash/range');
const {fontTheme} = require('./src/common/shared/theme/fontTheme');
const {colorTheme} = require('./src/common/shared/theme/colorTheme');
const calculateTalkViewerPx = require('./tailwindcss/functions/calculateTalkViewerPx');
const pxToRem = require('./tailwindcss/functions/pxToRem');

module.exports = {
  darkMode: false,
  theme: {
    extend: {
      colors: () => {
        const colors = {};
        flattenDeep(values(colorTheme)).forEach(({tw, color}) => {
          colors[tw] = color;
        });
        return colors;
      },
      opacity: {
        15: '.15',
      },
      maxWidth: {
        '212pxr': pxToRem(212),
        '360pxr': pxToRem(360),
        '640pxr': pxToRem(640),
        '720pxr': pxToRem(720),
        '926pxr': pxToRem(926),
        '956pxr': pxToRem(956),
        '1200pxr': pxToRem(1200),
        '300tvs': calculateTalkViewerPx(300),
        '474tvs': calculateTalkViewerPx(474),
        '567tvs': calculateTalkViewerPx(567),
        '660tvs': calculateTalkViewerPx(660),
      },
      minWidth: {
        '1pxr': pxToRem(1),
        '44pxr': pxToRem(44),
        '37pxr': pxToRem(37),
        '172pxr': pxToRem(172),
        '192pxr': pxToRem(192),
        '1200pxr': pxToRem(1200),
        '62tvs': calculateTalkViewerPx(62),
        '68tvs': calculateTalkViewerPx(68),
        '72tvs': calculateTalkViewerPx(72),
        '76tvs': calculateTalkViewerPx(76),
        '78tvs': calculateTalkViewerPx(78),
        '110tvs': calculateTalkViewerPx(110),
        '250tvs': calculateTalkViewerPx(250),
      },
      minHeight: {
        '98pxr': pxToRem(98),
        '108pxr': pxToRem(108),
        '160pxr': pxToRem(160),
        '172pxr': pxToRem(172),
        '180pxr': pxToRem(180),
        '200pxr': pxToRem(200),
        '270pxr': pxToRem(270),
        '280pxr': pxToRem(280),
        '340pxr': pxToRem(340),
        '360pxr': pxToRem(360),
        '364pxr': pxToRem(364),
        '500pxr': pxToRem(500),
      },
      maxHeight: {
        '9/10': '90%',
        '7/10': '70%',
      },
      borderRadius: {
        '2pxr': pxToRem(2),
        '3pxr': pxToRem(3),
        '4pxr': pxToRem(4),
        '6pxr': pxToRem(6),
        '5pxr': pxToRem(5),
        '8pxr': pxToRem(8),
        '10pxr': pxToRem(10),
        '12pxr': pxToRem(12),
        '16pxr': pxToRem(16),
        '18pxr': pxToRem(18),
        '20pxr': pxToRem(20),
        '22pxr': pxToRem(22),
        '24pxr': pxToRem(24),
        '32pxr': pxToRem(32),
        '48pxr': pxToRem(48),
        '90pxr': pxToRem(90),
        '100pxr': pxToRem(100),
        '150pxr': pxToRem(150),
        '4tvs': calculateTalkViewerPx(4),
        '6tvs': calculateTalkViewerPx(6),
        '8tvs': calculateTalkViewerPx(8),
        '26tvs': calculateTalkViewerPx(26),
        '40tvs': calculateTalkViewerPx(40),
      },
      width: {
        'fit-content': 'fit-content',
      },
      zIndex: {
        1: '1',
        gradient: 10,
        100: '100',
        110: '110',
      },
      borderWidth: {
        0.5: '0.5px',
        1: '1px',
      },
      keyframes: {
        fadeIn: {
          from: {
            opacity: '0',
          },
          to: {
            opacity: '1',
          },
        },
        loadingSpin: {
          from: {
            transform: 'rotate(0deg)',
          },
          to: {
            transform: 'rotate(359deg)',
          },
        },
        bounceScale: {
          '0%': {
            transform: 'scale(0.5)',
          },
          '35%': {
            transform: 'scale(1.07)',
          },
          '80%, 100%': {
            transform: 'scale(1)',
          },
        },
      },
      animation: {
        fadeIn: 'fadeIn 0.2s linear forwards',
        loadingSpin: 'loadingSpin 1.5s linear infinite',
        bounceScale: 'bounceScale 0.5s linear forwards',
      },
      boxShadow: {
        round: '4px 6px 15px rgba(0,0,0,0.08)',
        dialog: '0 0 24px rgba(0,0,0,0.18)',
      },
    },
    fonts: () => {
      const fonts = {};
      fontTheme.forEach(({tw}) => {
        fonts[tw] = tw;
      });
      return fonts;
    },
    screens: {
      shortcut: '280px',
      // 단일 string을 넣는 것은 min-width를 의미한다. '모바일 우선'프로젝트에서는 min-width 사용이 유리하다고 하여 min-width로 break-point를 설정함
      mobile: '360px',
      foldable: '523px',
      tablet: '768px',
      // 폴더블폰 기준 아래에 해당하는 요소만 선택할 때 사용
      'under-foldable': {max: '522px'},
      // tablet 기준 아래에 해당하는 요소만 선택할 때 사용
      'under-tablet': {max: '767px'},
      // mobile 기준 아래에 해당하는 요소만 선택할 때 사용
      'under-mobile': {max: '359px'},
      // mobile 기준 아래에 해당하는 요소만 선택할 때 사용
      'under-320-view': {max: '320px'},
      // 모바일 메인/서브홈 네비바 우측 아이콘이 축소되는 기준 값
      'under-foldable-front': {max: '279px'},
      // 콘텐츠 홈 썸네일 크기 분기를 위해 사용
      'under-375-view': {min: '360px', max: '374px'},
      'under-390-view': {min: '375px', max: '389px'},
      'under-490-view': {min: '390px', max: '489px'},
      'under-640-view': {min: '490px', max: '639px'},
    },
    // 기본 tailwind space 는 무시함(아직 디자인 시스템에 대한 성숙도가 낮아 px 기반 디자인 가이드를 따라야하기 때문에 pxr, px 생성)
    spacing: {
      0: '0px',
      px: '1px',
      // pxr to rem(16px 기준)
      ...range(1, 100).reduce((acc, px) => {
        acc[`${px}pxr`] = pxToRem(px);
        return acc;
      }, {}),
      // add specific pxr
      '1.5pxr': pxToRem(1.5),
      '7.5pxr': pxToRem(7.5),
      '26.5pxr': pxToRem(26.5),
      '94pxr': pxToRem(94),
      '100pxr': pxToRem(100),
      '101pxr': pxToRem(101),
      '112pxr': pxToRem(112),
      '116pxr': pxToRem(116),
      '102pxr': pxToRem(102),
      '103pxr': pxToRem(103),
      '104pxr': pxToRem(104),
      '108pxr': pxToRem(108),
      '110pxr': pxToRem(110),
      '118pxr': pxToRem(118),
      '120pxr': pxToRem(120),
      '122pxr': pxToRem(122),
      '127.5pxr': pxToRem(127.5),
      '128pxr': pxToRem(128),
      '132pxr': pxToRem(132),
      '136pxr': pxToRem(136),
      '138pxr': pxToRem(138),
      '140pxr': pxToRem(140),
      '150pxr': pxToRem(150),
      '151pxr': pxToRem(151),
      '151.83pxr': pxToRem(151.83),
      '152pxr': pxToRem(152),
      '153pxr': pxToRem(153),
      '156pxr': pxToRem(156),
      '157pxr': pxToRem(157),
      '160pxr': pxToRem(160),
      '162pxr': pxToRem(162),
      '168pxr': pxToRem(168),
      '169pxr': pxToRem(169),
      '170pxr': pxToRem(170),
      '176pxr': pxToRem(176),
      '180pxr': pxToRem(180),
      '183pxr': pxToRem(183),
      '184pxr': pxToRem(184),
      '192pxr': pxToRem(192),
      '195pxr': pxToRem(195),
      '200pxr': pxToRem(200),
      '202pxr': pxToRem(202),
      '204pxr': pxToRem(204),
      '216pxr': pxToRem(216),
      '220pxr': pxToRem(220),
      '223pxr': pxToRem(223),
      '232pxr': pxToRem(232),
      '241pxr': pxToRem(241),
      '250pxr': pxToRem(250),
      '253pxr': pxToRem(253),
      '272pxr': pxToRem(272),
      '274pxr': pxToRem(274),
      '276pxr': pxToRem(276),
      '288pxr': pxToRem(288),
      '300pxr': pxToRem(300),
      '320pxr': pxToRem(320),
      '308pxr': pxToRem(308),
      '326pxr': pxToRem(326),
      '330pxr': pxToRem(330),
      '345pxr': pxToRem(345),
      '356pxr': pxToRem(356),
      '360pxr': pxToRem(360),
      '364pxr': pxToRem(364),
      '394pxr': pxToRem(394),
      '400pxr': pxToRem(400),
      '448pxr': pxToRem(448),
      '478pxr': pxToRem(478),
      '500pxr': pxToRem(500),
      '632pxr': pxToRem(632),
      '956pxr': pxToRem(956),
      '1200pxr': pxToRem(1200),
      // px to px
      ...range(1, 100).reduce((acc, px) => {
        acc[`${px}px`] = `${px}px`;
        return acc;
      }, {}),
      // add specific px
      '51px': '51px',
      '52px': '52px',
      '56px': '56px',
      '80px': '80px',
      '101px': '101px',
      '150px': '150px',
      '512px': '512px',
      '20vh': '20vh',
      ...range(1, 200).reduce((acc, px) => {
        // 톡뷰어에 사용할 talk viewer size unit을 정의합니다.
        acc[`${px}tvs`] = calculateTalkViewerPx(px);
        return acc;
      }, {}),
      '19.5tvs': calculateTalkViewerPx(19.5),
      '20.5tvs': calculateTalkViewerPx(20.5),
      '24.5tvs': calculateTalkViewerPx(24.5),
      '26.5tvs': calculateTalkViewerPx(26.5),
      '239tvs': calculateTalkViewerPx(239),
      '240tvs': calculateTalkViewerPx(240),
      '250tvs': calculateTalkViewerPx(250),
      '300tvs': calculateTalkViewerPx(300),
      '360tvs': calculateTalkViewerPx(360),
      '426tvs': calculateTalkViewerPx(426),
      '430tvs': calculateTalkViewerPx(430),
      '567tvs': calculateTalkViewerPx(567),
      '570tvs': calculateTalkViewerPx(570),
      '660tvs': calculateTalkViewerPx(660),
      '690tvs': calculateTalkViewerPx(690),
      '960tvs': calculateTalkViewerPx(960),
      '4tvs-minus': calculateTalkViewerPx(-4),
      '6tvs-minus': calculateTalkViewerPx(-6),
      '10tvs-minus': calculateTalkViewerPx(-10),
      '13.5tvs-minus': calculateTalkViewerPx(-13.5),
      '20tvs-minus': calculateTalkViewerPx(-20),
      '45tvs-minus': calculateTalkViewerPx(-45),
      '93tvs-minus': calculateTalkViewerPx(-93),
      mobile_header_height_px: '56px',
      pc_header_height_px: '96px',
      pc_viewer_header_height_px: '68px',
      'roulette-content-height-px': '584px',
    },
    fontSize: {
      '22tvs': calculateTalkViewerPx(22),
      '24tvs': calculateTalkViewerPx(24),
      '26tvs': calculateTalkViewerPx(26),
      '28tvs': calculateTalkViewerPx(28),
      '30tvs': calculateTalkViewerPx(30),
      '34tvs': calculateTalkViewerPx(34),
    },
    blur: {
      '20px': '20px',
      '30px': '30px',
      '80px': '80px',
    },
  },
  plugins: [
    require('./tailwindcss/plugins/scrollbar-hide'),
    require('./tailwindcss/plugins/grid-inner-border-cols'),
    require('./tailwindcss/plugins/inner-border-color'),
    require('./tailwindcss/plugins/inner-border-radius'),
    require('./tailwindcss/plugins/safe-area-inset'),
    require('./tailwindcss/plugins/inner-border'),
    require('./tailwindcss//plugins/flex-center'),
    plugin(({addComponents, addUtilities, addVariant}) => {
      const fonts = {};
      fontTheme.forEach(({tw, fontSize, fontWeight, lineHeight}) => {
        fonts['.' + tw] = {
          fontSize,
          fontWeight,
          lineHeight,
        };
      });
      addComponents(fonts);
      addUtilities({
        '.use-gpu': {
          transform: 'translate3d(0, 0, 0)',
          'will-change': 'transform',
        },
        /**
         * 특수 문자에서 word break가 일어나지 않아 word-break를 추가합니다. break-word는 deprecated 될 예정이기 때문에 overflowWrap anywhere도 추가해주었습니다.
         * https://github.com/tailwindlabs/tailwindcss/discussions/2213
         */
        '.break-word-anywhere': {
          'word-break': 'break-word',
          'overflow-wrap': 'anywhere',
        },
        /**
         * 공백 기준으로만 개행을 하되 overflow 시 상위 wrapper 크기를 넘어가지 않도록 제한합니다.
         */
        '.keep-word-anywhere': {
          'word-break': 'keep-all',
          'overflow-wrap': 'anywhere',
        },
        '.blind': {
          position: 'absolute',
          overflow: 'hidden',
          border: 0,
          width: '1px',
          height: '1px',
          margin: '-1px',
          clip: 'rect(0, 0, 0, 0)',
          'clip-path': 'polygon(0 0, 0 0, 0 0)',
        },
      });
      addVariant('all-child', '& > *');
      addVariant('all-child-active', '&:active > *');
    }),
  ],
};
